import React from "react";
import { Row, Col, Tag, Badge, Button, Popconfirm } from "antd";
import { EditOutlined, MoreOutlined, CopyOutlined } from "@ant-design/icons";
import { MdDeleteOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import PageRoutes from "../../../utils/routes";

const JobCard = ({ advertDetails, onClick, deleteAdvert }) => {
  const navigate = useNavigate();

  const editAdvert = () => {
    navigate(PageRoutes.editAdvert, {
      state: { advertId: advertDetails.id },
    });
  };

  const copyAdvert = () => {
    navigate(PageRoutes.copyAdvert, {
      state: { advertId: advertDetails.id },
    });
  };

  const TagGroup = ({ tags }) => (
    <div className="tag-container">
      {tags.map((tag, index) => (
        <Tag key={index} className="job-tag">
          {tag}
        </Tag>
      ))}
    </div>
  );

  const StatItem = ({ label, value }) => (
    <div className="stat-item">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );

  const mainTags = [
    advertDetails.sector.name,
    advertDetails.position.name,
    advertDetails.department.name,
    advertDetails?.startDate,
    advertDetails?.endDate,
    advertDetails.wageType.description,
    `${advertDetails.wagePeriod.description} Maaş`,
    advertDetails?.wage
      ? `${advertDetails.wage} TRY`
      : `${advertDetails.minWage}- ${advertDetails.maxWage} TRY`,
  ].filter(Boolean);

  return (
    <div className="job-card">
      <div className="job-card-header">
        <div className="main-tags">
          <TagGroup tags={mainTags} />
        </div>
        <div className="action-buttons">
          <Popconfirm
            title="İlanı Sil"
            description="Bu İlanı silmek istediğinize emin misiniz?"
            onConfirm={() => deleteAdvert(advertDetails.id)}
            okText="Evet"
            cancelText="Hayır">
            <Button danger className="action-button">
              <MdDeleteOutline />
              İlanı Sil
            </Button>
          </Popconfirm>
          <Button
            onClick={() => onClick(advertDetails.id)}
            className="action-button">
            <MoreOutlined />
            Detayları Gör
          </Button>
        </div>
      </div>

      <div className="job-card-content">
        <div className="job-info">
          <h2 className="job-title">{advertDetails.title}</h2>
          <div className="job-id">
            <Badge
              status={advertDetails.isActive ? "success" : "default"}
              className="status-badge"
            />
            {advertDetails.id}
          </div>
        </div>

        <div className="job-stats">
          <StatItem
            label="İlan Tipi"
            value={advertDetails.jobType.description}
          />
          <StatItem
            label="Dönem"
            value={advertDetails.durationType.description}
          />
          <StatItem label="Süre" value={advertDetails.jobDuration} />
          <StatItem label="Başvuru" value={advertDetails.applicants.length} />
        </div>
      </div>

      <div className="job-card-footer">
        <div className="keywords">
          <TagGroup tags={advertDetails?.keywords || []} />
        </div>
        <div className="edit-buttons">
          <Button onClick={editAdvert} className="edit-button">
            <EditOutlined />
            İlanı Düzenle
          </Button>
          <Button onClick={copyAdvert} className="edit-button">
            <CopyOutlined />
            İlanı Kopyala
          </Button>
        </div>
      </div>

      <style jsx>{`
        .job-card {
          background: white;
          border-radius: 8px;
          border: 1px solid #e8e8e8;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          padding: 20px;
          margin-bottom: 16px;
          transition: all 0.3s ease;
        }

        .job-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        }

        .job-card-header {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 24px;
          justify-content: space-between;
        }

        .main-tags {
          flex: 1;
          min-width: 280px;
        }

        .tag-container {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .job-tag {
          margin: 0;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .job-card-content {
          display: grid;
          grid-template-columns: 1fr;
          gap: 24px;
          margin-bottom: 24px;
        }

        @media (min-width: 768px) {
          .job-card-content {
            grid-template-columns: 1fr 2fr;
          }
        }

        .job-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .job-title {
          font-size: 20px;
          font-weight: 600;
          margin: 0;
          color: #2c3e50;
        }

        .job-id {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #666;
        }

        .job-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 16px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 6px;
          transition: all 0.3s ease;
        }

        .stat-item:hover {
          background: #f1f3f5;
        }

        .stat-value {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
        }

        .stat-label {
          font-size: 13px;
          color: #6c757d;
          margin-top: 4px;
        }

        .job-card-footer {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          justify-content: space-between;
          align-items: center;
        }

        .keywords {
          flex: 1;
          min-width: 280px;
        }

        .edit-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .edit-button {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        @media (max-width: 576px) {
          .job-card {
            padding: 16px;
          }

          .job-card-header,
          .job-card-footer {
            flex-direction: column;
          }

          .action-buttons,
          .edit-buttons {
            width: 100%;
            justify-content: flex-start;
          }

          .stat-item {
            padding: 8px;
          }

          .stat-value {
            font-size: 16px;
          }

          .stat-label {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default JobCard;
